/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @file paddle 教程 hooks
 * @author FengGuang(fengguang01@baidu.com)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest, useMount, useUpdateEffect } from 'react-use';
import throttle from 'lodash/throttle';

import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import useRateHooks from '../../components/document-rate/rateHooks';
import { ITutorialsLessonItem, ITutorialsProjectItem } from './type';

import { fetchGetProjectContent, getCacheData, getProjectMap } from '../../api/pages/tutorialsApi';
import useRightMenuHooks from './components/right-menu/rightMenuHooks';
import useMenuHooks from './components/menu/menuHooks';

const useProjectContentHooks = () => {
    const match = useRouteMatch();

    const [content, setContent] = useState<Object>({});
    const [loading, setLoading] = useState(false);

    const projectId = (match.params as any).id || 0;
    useEffect(() => {
        (async () => {
            if (!projectId) {
                return;
            }

            let res;
            setLoading(true);
            try {
                res = await fetchGetProjectContent({ projectId });
            } catch (err: any) {
                // ignre
            }
            setLoading(false);
            if (res?.body) {
                let content = {};
                try {
                    content = JSON.parse(res.body.result);
                } catch (err: any) {
                    // ignore
                }
                setContent(content);
            }
        })();
    }, [projectId, setLoading]);

    return {
        content,
        loading
    };
};

const useTutorialsHooks = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const location = useLocation();

    const projectId = (match.params as any).id || '';

    const projectContentHooks = useProjectContentHooks();
    // 切换
    useEffect(() => {
        if (history.action === 'PUSH') {
            window.scrollTo({ top: 0 });
        }
    }, [history, projectContentHooks.content]);

    const rightMenuHooks = useRightMenuHooks(projectContentHooks.content);
    const menuHooks = useMenuHooks();
    // 获取对应group的课程
    const {
        groupSelectedKey,
        versionSelectedKey,
        versionList,
        groupMapByVersionId,
        lessonMapByGroupId,
        projectMapByLessonId,
        setLessonToGroup,
        setProjectToLesson,
        onLessionSelect,
        onLessonOpenKeysChange,
        onVersionChange
    } = menuHooks;
    const openSelectedMenuRef = useLatest(menuHooks.openSelectedMenu);
    const openSelectedMenuThrottle = useMemo(() => {
        return throttle(() => {
            openSelectedMenuRef.current();
        }, 10);
    }, [openSelectedMenuRef]);

    // 初始化projectMapData数据
    const [firstStatus, setFirstStatus] = useState<boolean>(true);
    const [projectMapData] = useState<Promise<string>>(async () => {
        const projectId = location.pathname.split('/').pop();
        if (projectId && !Number.isNaN(Number(projectId))) {
            let getProjectMapData = await getProjectMap();
            const projectMapHash = getProjectMapData.body as any;
            const version = projectMapHash[projectId]?.version;
            if (version !== '2.3') {
                onVersionChange(version as string);
            }
            setTimeout(() => {
                openSelectedMenuThrottle();
                onLessionSelect(Array.of(projectId as string));
                historyReplace(`/tutorials/projectdetail/${projectId}`);
                setTimeout(() => {
                    setFirstStatus(false);
                }, 50);
            }, 1200);
            return projectId as string;
        }
        setFirstStatus(false);
        return '';
    });

    const getLessonAndProject = useCallback(
        async (isMounted: boolean = false) => {
            const getLessonCacheData = async (params: { version: string; tab: number }) => {
                let res;
                try {
                    res = await getCacheData({ ...params });
                } catch (err: any) {
                    // ignore
                }
                if (!res) {
                    return;
                }
                const list: any[] = res.body.result || [];
                const theLessonList: ITutorialsLessonItem[] = list.map(item => ({
                    id: '' + item.id,
                    title: item.lessonName,
                    projectList: item.projectList
                }));
                return theLessonList;
            };

            const handleProjectList = (list: any[]) => {
                const theProjectList: ITutorialsProjectItem[] = list.map(item => ({
                    id: '' + item.projectId,
                    title: item.projectName,
                    link: `/tutorials/projectdetail/${item.projectId}`,
                    lessonProjectId: item.lessonProjectId
                }));
                return theProjectList;
            };
            // 这里设置version,groupId,lessonId,selectId
            const tempList = versionList.filter(item => item.version === versionSelectedKey);
            await Promise.all(
                tempList.map(async version => {
                    await Promise.all(
                        groupMapByVersionId[version.version]?.map(async (group, index) => {
                            let lessonList = (await getLessonCacheData({ version: version.version, tab: index })) || [];
                            if (group.id === '1297') {
                                lessonList = lessonList.filter(i => i.title.indexOf('预习') < 0);
                            }
                            setLessonToGroup(group.id, lessonList);
                            lessonList.forEach(lesson => {
                                const projectList = handleProjectList(lesson.projectList ?? []);
                                setProjectToLesson(lesson.id, projectList);
                                isMounted && openSelectedMenuThrottle();
                            });
                        })
                    );
                })
            );
        },
        [
            versionList,
            groupMapByVersionId,
            setLessonToGroup,
            setProjectToLesson,
            openSelectedMenuThrottle,
            versionSelectedKey
        ]
    );

    useMount(() => {
        getLessonAndProject(true);
    });
    useUpdateEffect(() => {
        getLessonAndProject(false);
    }, [getLessonAndProject, versionSelectedKey]);

    // 当url没有指向某个project的时候，自动选择一个project并跳转过去
    const matchPath = match.path;
    const historyReplace = history.replace;
    useEffect(() => {
        if (matchPath === '/tutorials') {
            const groupId = groupMapByVersionId[versionSelectedKey]?.[0]?.id;
            const lessonId = lessonMapByGroupId[groupId]?.[0]?.id;
            const projectId = projectMapByLessonId[lessonId]?.[0]?.id;
            if (projectId) {
                historyReplace(`/tutorials/projectdetail/${projectId}`);
            }
        }

        // 切换菜单时需要多加一个处理逻辑，自动定位到菜单第一个元素
        if (matchPath !== '/tutorials' && matchPath.includes('tutorials') && !firstStatus) {
            console.log(projectMapData);
            let groupId = groupSelectedKey || '1297';
            const lessonId = lessonMapByGroupId[groupId]?.[0]?.id;
            const projectId = projectMapByLessonId[lessonId]?.[0]?.id;
            onLessonOpenKeysChange(Array.of(lessonId));
            onLessionSelect(Array.of(projectId));
            if (projectId) {
                historyReplace(`/tutorials/projectdetail/${projectId}`);
            }
        }
    }, [
        groupSelectedKey,
        matchPath,
        versionSelectedKey,
        groupMapByVersionId,
        lessonMapByGroupId,
        projectMapByLessonId,
        historyReplace,
        onLessonOpenKeysChange,
        onLessionSelect
    ]);

    const rateHook = useRateHooks();

    const breadcrumbsList = menuHooks.breadcrumbsList;
    const breadcrumbsListHook = useMemo(
        () => ({
            breadcrumbsList
        }),
        [breadcrumbsList]
    );

    // 根据 hash 跳转到锚点位置
    const scrollToAnchor = useCallback(() => {
        const anchorName = location.hash.slice(1);
        if (!anchorName) {
            return;
        }
        const anchorDom = document.getElementById(anchorName) || document.querySelector(`a[name="${anchorName}"]`);
        if (anchorDom) {
            anchorDom.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [location.hash]);
    useEffect(scrollToAnchor, [location.hash, projectContentHooks.content, scrollToAnchor]);

    const lessonProjectId = menuHooks.lessonProjectId;

    return {
        projectId,
        lessonProjectId,

        projectContentHooks,
        menuHooks,
        rightMenuHooks,
        breadcrumbsListHook,
        rateHook
    };
};

export default useTutorialsHooks;
